// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import useApp from "antd/es/app/useApp";
import Button from "antd/es/button/button";
import React, { useCallback } from "react";

import { type ApiError, logApiError, parseApiError } from "./apiError";

interface ShowErrorOptions {
  message: string;
  /** When provided, the error notification includes a Retry button. */
  onRetry?: () => void;
}

interface ShowWarningOptions {
  message: string;
}

/**
 * Hook that returns helpers for surfacing API errors as Ant Design
 * notifications.
 *
 * Must be called inside the `<AntdApp>` provider tree (which is already
 * set up in `App.tsx`).
 *
 * When an `onRetry` callback is supplied, the notification includes a
 * **Retry** button so users can re-attempt the failed operation without
 * navigating away.
 *
 * Usage:
 * ```ts
 * const { showError, handleError } = useApiError();
 *
 * // Show a pre-parsed ApiError:
 * showError({ message: apiError.message });
 *
 * // With retry:
 * showError({ message: "Save failed", onRetry: () => save(data) });
 *
 * // Or parse + show an unknown error in one call:
 * handleError(caughtError, "/api/some/endpoint");
 * ```
 */
export function useApiError() {
  const { notification } = useApp();

  /** Display a structured ApiError as a notification. */
  const showError = useCallback(
    (error: ApiError | ShowErrorOptions) => {
      const onRetry = "onRetry" in error ? error.onRetry : undefined;
      const btn = onRetry
        ? React.createElement(
            Button,
            { type: "primary", size: "small", onClick: onRetry },
            "Retry"
          )
        : undefined;
      notification.error({
        message: error.message,
        btn,
        duration: onRetry ? 8 : 4.5,
      });
    },
    [notification]
  );

  /**
   * For a state that is not a failure and that retrying cannot resolve — e.g. an
   * administrator policy withholding a permission. Kept distinct from
   * `showError` so a policy state is not dressed up in red as if it broke.
   *
   * Takes no `onRetry`: retry is exactly what cannot help here.
   *
   * Does not log — it only ever sees the translated display string. Log the
   * underlying error at the call site, where the server's own message (which
   * names the offending host) is still in hand.
   */
  const showWarning = useCallback(
    ({ message }: ShowWarningOptions) => {
      notification.warning({
        message,
        duration: 4.5,
      });
    },
    [notification]
  );

  /**
   * Parse an unknown error and display it as a notification.
   * Also logs the error to the console via `logApiError`.
   *
   * Silently ignores AbortError from component unmounts so that
   * cancelled requests don't trigger confusing error toasts.
   *
   * @param onRetry — when provided, the notification includes a Retry button.
   */
  const handleError = useCallback(
    (error: unknown, endpoint?: string, onRetry?: () => void) => {
      // Don't show toasts for intentional abort (unmount / navigation)
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      const parsed = parseApiError(error, endpoint);
      logApiError(endpoint ?? "API", error);
      if (onRetry) {
        showError({ message: parsed.message, onRetry });
      } else {
        showError(parsed);
      }
    },
    [showError]
  );

  return { showError, showWarning, handleError };
}
